import React, { useState, useRef, useEffect } from "react";
import "./signUp.css";
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import {
  Email,
  Person,
  Lock,
  Phone,
  DirectionsCar,
  Badge,
  Visibility,
  VisibilityOff,
  RocketLaunch,
  Wc,
  MarkEmailRead,
  RefreshOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const SignUp = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  
  // OTP related states
  const [showOtpBox, setShowOtpBox] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [timer, setTimer] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  const [formData, setFormData] = useState({
    fullName: "",
    gender: "",
    phone: "",
    email: "",
    vehicleType: "",
    licenseNumber: "",
    vehicleNumber: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");

  const togglePassword = () => setShowPassword(!showPassword);
  const toggleConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const handleSignInClick = () => navigate("/");

  // Timer countdown for OTP
  useEffect(() => {
    if (showOtpBox && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, showOtpBox]);

  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setApiError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          profileImage: "File size should be less than 2MB",
        }));
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          profileImage: "Only JPEG, JPG, PNG, and GIF files are allowed",
        }));
        return;
      }

      setSelectedFile(file);
      setErrors((prev) => ({ ...prev, profileImage: "" }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // OTP Input Handlers
  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setApiError("");

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill("")]);

      const nextEmpty = newOtp.length < 6 ? newOtp.length : 5;
      inputRefs.current[nextEmpty]?.focus();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = "Name must be at least 2 characters long";
    }

    if (!formData.gender) {
      newErrors.gender = "Please select your gender";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.vehicleType) {
      newErrors.vehicleType = "Please select a vehicle type";
    }

    const requiresLicense =
      formData.vehicleType === "Bike" || formData.vehicleType === "Car";

    if (requiresLicense) {
      if (!formData.licenseNumber.trim()) {
        newErrors.licenseNumber = "License number is required for Bike/Car";
      } else if (!/^[A-Z]{2}[-\s]?\d{13,15}$/i.test(formData.licenseNumber)) {
        newErrors.licenseNumber = "Please enter a valid license number";
      }
    }

    if (requiresLicense) {
      if (!formData.vehicleNumber.trim()) {
        newErrors.vehicleNumber = "Vehicle number is required for Bike/Car";
      } else {
        const cleanedVehicleNumber = formData.vehicleNumber.replace(
          /[\s-]/g,
          ""
        );
        if (
          !/^[A-Z]{2}[0-9]{1,2}[A-Z]{1,2}[0-9]{4}$/i.test(cleanedVehicleNumber)
        ) {
          newErrors.vehicleNumber =
            "Please enter a valid vehicle number (e.g., WB01AB1234)";
        }
      }
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    setApiSuccess("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append("fullName", formData.fullName);
      submitData.append("gender", formData.gender);
      submitData.append("phone", formData.phone);
      submitData.append("email", formData.email);
      submitData.append("vehicleType", formData.vehicleType);
      submitData.append("password", formData.password);
      submitData.append("confirmPassword", formData.confirmPassword);

      const requiresLicense =
        formData.vehicleType === "Bike" || formData.vehicleType === "Car";
      if (requiresLicense) {
        submitData.append("licenseNumber", formData.licenseNumber);
        submitData.append("vehicleNumber", formData.vehicleNumber);
      }

      if (selectedFile) {
        submitData.append("profileImage", selectedFile);
      }

      const response = await axios.post(
        "https://api.rismicmart.com/api/delivery-person/signup",
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setApiSuccess(
          response.data.message || "OTP sent to your email! Please verify."
        );
        setUserId(response.data.userId);
        
        // Show OTP box after 1 second
        setTimeout(() => {
          setShowOtpBox(true);
          setApiSuccess("");
          // Focus on first OTP input
          setTimeout(() => {
            inputRefs.current[0]?.focus();
          }, 100);
        }, 1000);
      }
    } catch (error) {
      console.error("Signup error:", error);

      if (error.response?.data) {
        if (error.response.data.errors) {
          const backendErrors = {};
          error.response.data.errors.forEach((err) => {
            backendErrors[err.param || err.path] = err.msg || err.message;
          });
          setErrors(backendErrors);
        }
        setApiError(
          error.response.data.message ||
            "Registration failed. Please try again."
        );
      } else {
        setApiError(
          "Network error. Please check your connection and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setApiError("");
    setApiSuccess("");

    const otpString = otp.join("");

    if (otpString.length !== 6) {
      setApiError("Please enter complete 6-digit OTP");
      return;
    }

    setOtpLoading(true);

    try {
      const response = await axios.post(
        "https://api.rismicmart.com/api/delivery-person/verify-otp",
        {
          userId,
          otp: otpString,
        }
      );

      if (response.data.success) {
        setApiSuccess(
          response.data.message || "Email verified successfully!"
        );

        setTimeout(() => {
          navigate("/", { replace: true });
        }, 2000);
      }
    } catch (error) {
      console.error("OTP verification error:", error);

      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError("Verification failed. Please try again.");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setApiError("");
    setApiSuccess("");
    setResendLoading(true);

    try {
      const response = await axios.post(
        "https://api.rismicmart.com/api/delivery-person/resend-otp",
        {
          userId,
          email: formData.email,
        }
      );

      if (response.data.success) {
        setApiSuccess("New OTP sent to your email!");
        setOtp(["", "", "", "", "", ""]);
        setTimer(600);
        setCanResend(false);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setApiError("Failed to resend OTP. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToSignup = () => {
    setShowOtpBox(false);
    setOtp(["", "", "", "", "", ""]);
    setTimer(600);
    setCanResend(false);
    setApiError("");
    setApiSuccess("");
  };

  const showLicenseFields =
    formData.vehicleType === "Bike" || formData.vehicleType === "Car";

  return (
    <div className="signup-container">
      <div className="signup-card">
        {!showOtpBox ? (
          <>
            <h2 className="signup-title">
              Create Your Account <RocketLaunch className="signup-icon" />
            </h2>

            {apiError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {apiError}
              </Alert>
            )}

            {apiSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {apiSuccess}
              </Alert>
            )}

            <form className="signup-form" onSubmit={handleSubmit}>
              <div className="signup-grid">
                <TextField
                  label="Full Name"
                  name="fullName"
                  variant="outlined"
                  fullWidth
                  value={formData.fullName}
                  onChange={handleChange}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person style={{ color: "#004c3f" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  select
                  label="Select Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  error={!!errors.gender}
                  helperText={errors.gender}
                  fullWidth
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Wc style={{ color: "#004c3f" }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </TextField>

                <TextField
                  label="Phone Number"
                  name="phone"
                  variant="outlined"
                  fullWidth
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone style={{ color: "#004c3f" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Email ID"
                  name="email"
                  variant="outlined"
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email style={{ color: "#004c3f" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  select
                  label="Select Vehicle"
                  name="vehicleType"
                  value={formData.vehicleType}
                  onChange={handleChange}
                  error={!!errors.vehicleType}
                  helperText={errors.vehicleType}
                  fullWidth
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DirectionsCar style={{ color: "#004c3f" }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="Battery Cycle">Battery Cycle</MenuItem>
                  <MenuItem value="Battery Bike">Battery Bike</MenuItem>
                  <MenuItem value="Bike">Bike</MenuItem>
                  <MenuItem value="Car">Car</MenuItem>
                </TextField>

                {showLicenseFields && (
                  <>
                    <TextField
                      label="Licence Number"
                      name="licenseNumber"
                      variant="outlined"
                      fullWidth
                      value={formData.licenseNumber}
                      onChange={handleChange}
                      error={!!errors.licenseNumber}
                      helperText={errors.licenseNumber}
                      className="signup-input"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Badge style={{ color: "#004c3f" }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Vehicle Number"
                      name="vehicleNumber"
                      variant="outlined"
                      fullWidth
                      value={formData.vehicleNumber}
                      onChange={handleChange}
                      error={!!errors.vehicleNumber}
                      helperText={errors.vehicleNumber}
                      className="signup-input"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DirectionsCar style={{ color: "#004c3f" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}

                <TextField
                  label="Password"
                  name="password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  className="signup-input full-width"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock style={{ color: "#004c3f" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePassword}>
                          {showPassword ? (
                            <Visibility style={{ color: "#004c3f" }} />
                          ) : (
                            <VisibilityOff style={{ color: "#004c3f" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  variant="outlined"
                  type={showConfirmPassword ? "text" : "password"}
                  fullWidth
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  className="signup-input full-width"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock style={{ color: "#004c3f" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleConfirmPassword}>
                          {showConfirmPassword ? (
                            <Visibility style={{ color: "#004c3f" }} />
                          ) : (
                            <VisibilityOff style={{ color: "#004c3f" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className="image-upload">
                <label htmlFor="profileImage" className="image-label">
                  Upload Profile Image
                </label>
                <input
                  type="file"
                  id="profileImage"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                {errors.profileImage && (
                  <p
                    style={{ color: "red", fontSize: "12px", marginTop: "5px" }}
                  >
                    {errors.profileImage}
                  </p>
                )}
                {filePreview && (
                  <div style={{ marginTop: "10px" }}>
                    <img
                      src={filePreview}
                      alt="Preview"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                  </div>
                )}
                <p className="image-note">Image size should be less than 2MB</p>
              </div>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                className="signup-btn"
                disabled={loading}
                sx={{ mt: 2, position: "relative" }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                        left: "50%",
                        marginLeft: "-12px",
                      }}
                    />
                    SIGNING UP...
                  </>
                ) : (
                  "SIGN UP"
                )}
              </Button>

              <div className="signup-links">
                <p>
                  Already have an account?{" "}
                  <span className="signin-link" onClick={handleSignInClick}>
                    Sign In
                  </span>
                </p>
              </div>
            </form>
          </>
        ) : (
          <>
            {/* OTP Verification Box */}
            <Box className="otp-verification-box">
              <Box className="otp-header" sx={{ textAlign: "center", mb: 3 }}>
                <MarkEmailRead
                  sx={{ fontSize: 60, color: "#004c3f", mb: 2 }}
                />
                <Typography variant="h5" className="otp-title" sx={{ fontWeight: 700, color: "#004c3f", mb: 1 }}>
                  Verify Your Email
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  We've sent a 6-digit OTP to
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: "#004c3f" }}
                >
                  {formData.email}
                </Typography>
              </Box>

              {apiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {apiError}
                </Alert>
              )}

              {apiSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {apiSuccess}
                </Alert>
              )}

              <form onSubmit={handleVerifyOTP}>
                <Box
                  className="otp-inputs"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "10px",
                    mb: 2,
                  }}
                >
                  {otp.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                      inputProps={{
                        maxLength: 1,
                        style: {
                          textAlign: "center",
                          fontSize: "24px",
                          fontWeight: "600",
                          padding: "15px 10px",
                        },
                      }}
                      variant="outlined"
                      className="otp-input-field"
                      sx={{
                        width: "50px",
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#004c3f",
                          },
                        },
                      }}
                    />
                  ))}
                </Box>

                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography
                    variant="body2"
                    color={timer < 60 ? "error" : "textSecondary"}
                  >
                    {timer > 0
                      ? `Time remaining: ${formatTime(timer)}`
                      : "OTP expired"}
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  className="verify-otp-btn"
                  disabled={otpLoading || otp.join("").length !== 6}
                  sx={{
                    mt: 2,
                    mb: 2,
                    backgroundColor: "#004c3f",
                    "&:hover": { backgroundColor: "#00695c" },
                  }}
                >
                  {otpLoading ? (
                    <>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      VERIFYING...
                    </>
                  ) : (
                    "VERIFY OTP"
                  )}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={handleResendOTP}
                  disabled={!canResend || resendLoading}
                  startIcon={
                    resendLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshOutlined />
                    )
                  }
                  sx={{ color: "#004c3f", mb: 2 }}
                >
                  {resendLoading ? "Sending..." : "Resend OTP"}
                </Button>

                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2">
                    Wrong email?{" "}
                    <span
                      onClick={handleBackToSignup}
                      style={{
                        color: "#004c3f",
                        cursor: "pointer",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      Go Back
                    </span>
                  </Typography>
                </Box>
              </form>
            </Box>
          </>
        )}
      </div>
    </div>
  );
};

export default SignUp;
